import type { ButtonHTMLAttributes, AnchorHTMLAttributes, ReactNode } from "react";
import { cx } from "@/lib/cx";

type ButtonVariant = "primary" | "secondary" | "ghost";
type ButtonSize = "md" | "lg";

interface CommonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  orbit?: boolean;
  className?: string;
  children: ReactNode;
}

type ButtonAsButton = CommonProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, "className" | "children"> & {
    href?: undefined;
  };

type ButtonAsLink = CommonProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "className" | "children" | "href"> & {
    href: string;
  };

type ButtonProps = ButtonAsButton | ButtonAsLink;

function OrbitOverlay(): JSX.Element {
  return (
    <span aria-hidden="true" className="btn-orbit">
      <span className="btn-orbit-dot" />
    </span>
  );
}

export default function Button(props: ButtonProps): JSX.Element {
  const { variant = "primary", size = "md", orbit = false, className, children, ...rest } = props;
  const cls = cx("btn", `btn-${variant}`, size === "lg" && "btn-lg", className);

  if ("href" in rest && rest.href !== undefined) {
    const { href, ...anchorRest } = rest;
    const isExternal = /^https?:/.test(href);
    return (
      <a
        href={href}
        className={cls}
        {...(isExternal ? { rel: "noopener noreferrer" } : {})}
        {...anchorRest}
      >
        {orbit && <OrbitOverlay />}
        {children}
      </a>
    );
  }
  return (
    <button className={cls} {...(rest as ButtonHTMLAttributes<HTMLButtonElement>)}>
      {orbit && <OrbitOverlay />}
      {children}
    </button>
  );
}
